import {
  getCheckInTimelineApi,
  getCheckInQuestionsApi,
  getRecurrenceDetailsApi,
  updateRecurrenceDetailsApi,
  createOneOnOnesApi,
  getOneOnOnePreferencesApi,
  getStatsForEmployeeApi,
  getOneOnOnesListApi,
  getOneOnOneInstancesListApi,
  getOneOnOneGuideApi,
  saveOneOnOnePreferencesApi,
  getTalkingPointContinuousCommentsApi,
  getDefaultOneOnOneTemplateApi,
  putDefaultOneOnOneTemplateApi,
  archiveOneOnOneApi,
  unarchiveOneOnOneApi,
  getAllEmployeeFeedbacksApi,
  getOneOnOneChartApi,
} from 'api/OneOnOne';
import { createCommentApi, getCommentsApi, editCommentApi } from 'api/post';
import ColorCodedDate from 'models/common/ColorCodedDate';
import OneOnOneCard from 'models/oneOnOneCard';
import Post from 'models/post';
import PostFeedItemData from 'models/postFeedItemData';

import { POST_VISIBILITY_TYPES } from 'constants/ProjectConstants';

import { getApiBodyFromEditorStateAndMedia } from 'services/helpers/post';

class OneOnOneTimeline {
  constructor({
    id,
    status,
    stats,
    color,
    createdTS,
    completionTS,
    startTS,
    dueTS,
    createdTime,
    completionTime,
    startTime,
    dueTime,
    lastActivityTS,
    editorName,
  }) {
    this.id = id;
    this.createdTS = createdTS;
    this.dueTS = dueTS;
    this.completionTS = completionTS;
    this.status = status;
    this.stats = stats;
    this.startTS = startTS;
    this.color = color;
    this.createdTime = createdTime;
    this.completionTime = completionTime;
    this.startTime = startTime;
    this.dueTime = dueTime;
    this.lastActivityTS = lastActivityTS;
    this.editorName = editorName;
  }

  static fromJSON({
    id,
    status,
    stats,
    color,
    createdTS,
    completionTS,
    startTS,
    dueTS,
    createdTime,
    completionTime,
    startTime,
    dueTime,
    lastActivityTS,
    editorName,
  }) {
    return new OneOnOneTimeline({
      id,
      createdTS,
      dueTS,
      completionTS,
      startTS,
      status,
      color,
      stats: new OneOnOneTimeline.Stats({ ...stats }),
      createdTime: ColorCodedDate.fromJSON(createdTime),
      completionTime: ColorCodedDate.fromJSON(completionTime),
      startTime: ColorCodedDate.fromJSON(startTime),
      dueTime: ColorCodedDate.fromJSON(dueTime),
      lastActivityTS,
      editorName,
    });
  }

  static Stats = class {
    constructor({ comments, actionItems, rating, smiley }) {
      this.comments = comments;
      this.actionItems = actionItems;
      this.rating = rating;
      this.smiley = smiley;
    }
  };
}

class TalkingPoint {
  constructor({
    answerType,
    answerOptions,
    isPredefined,
    commentsUnReadCount,
    questionText,
    questionType,
    totalCommentCount,
    uuid,
    visibility,
    required,
    agendaItems,
  }) {
    this.answerType = answerType;
    this.answerOptions = answerOptions;
    this.isPredefined = isPredefined;
    this.commentsUnReadCount = commentsUnReadCount;
    this.questionText = questionText;
    this.questionType = questionType;
    this.totalCommentCount = totalCommentCount;
    this.uuid = uuid;
    this.visibility = visibility;
    this.required = required;
    if (agendaItems) {
      this.agendaItems = agendaItems;
    }
  }

  static fromJSON({
    answerType,
    answerOptions,
    isPredefined,
    commentsUnReadCount,
    questionText,
    questionType,
    totalCommentCount,
    uuid,
    visibility,
    required,
    agendaItems = null,
  }) {
    return new TalkingPoint({
      answerType,
      answerOptions,
      isPredefined,
      commentsUnReadCount,
      questionText,
      questionType,
      totalCommentCount,
      uuid,
      visibility: visibility === 'RECEIVER_AND_MANAGER' ? 'PUBLIC' : 'PRIVATE',
      required,
      agendaItems,
    });
  }
}

export const getOneOnOnePreferences = async () => {
  const result = await getOneOnOnePreferencesApi();
  return result.entity;
};

/**
 * @deprecated 8-04-2025
 */
export const createOneOnOnes = async ({ requestList }) => {
  const result = await createOneOnOnesApi({
    requestList,
  });
  return result;
};

export const getCheckInTimeline = async ({ checkInId, offset, limit }) => {
  const res = await getCheckInTimelineApi({ checkInId, offset, limit });
  return res.entity.map((timelineObj) => OneOnOneTimeline.fromJSON(timelineObj));
};

export const getCheckInQuestions = async ({ orgId, checkInId }) => {
  const res = await getCheckInQuestionsApi({ orgId, checkInId });
  return res.entity.map((talkingPoint) => TalkingPoint.fromJSON(talkingPoint));
};

export const getRecurrenceDetails = async ({ parentPostId }) => {
  const result = await getRecurrenceDetailsApi({ parentPostId });
  return result.entity;
};

export const updateRecurrenceDetails = async ({ checkInId, recurrentTask, recurrenceType, startTS, dueTS }) => {
  const result = await updateRecurrenceDetailsApi({
    checkInId,
    recurrentTask,
    recurrenceType,
    startTS,
    dueTS,
  });
  return result.entity;
};

export const getStatsForEmployee = async ({ employeeId }) => {
  const result = await getStatsForEmployeeApi({ employeeId });
  return result.entity;
};

export const getOneOnOnesList = async ({ employeeId, active = true }) => {
  const result = await getOneOnOnesListApi({ employeeId, active });
  return result.entity.map((card) => OneOnOneCard.fromJSON(card));
};

export const getChartsDataPoints = async ({ requestData }) => {
  const result = await getOneOnOneChartApi({ requestData });
  return result.entity;
};

export const getOneOnOneInstancesList = async ({ parentId, employeeId }) => {
  const result = await getOneOnOneInstancesListApi({ parentId, employeeId });
  return result.entity;
};

export const getOneOnOneGuide = async () => {
  const result = await getOneOnOneGuideApi();
  return result.entity;
};

export const saveOneOnOnePreferences = async ({ recurrenceId, recurrentTaskId, templateId }) => {
  const res = await saveOneOnOnePreferencesApi({ recurrenceId, recurrentTaskId, templateId });
  return res.entity;
};

export const getTalkingPointContinuousComments = async ({
  checkInId,
  offset,
  limit,
  scrollType = 'UPWARD', // can be 'UPWARD' || 'DOWNWARD'
  questionId,
  parentId,
  startTS,
  endTS,
}) => {
  const res = await getTalkingPointContinuousCommentsApi({
    checkInId,
    offset,
    limit,
    scrollType,
    questionId,
    parentId,
    startTS,
    endTS,
  });
  return res.entity.map(({ instanceId, dueTS, comment: commentObj }) => {
    const comment = new PostFeedItemData();
    comment.fromJSON({ post: commentObj });
    return { instanceId, dueTS, comment };
  });
};

export const getDefaultOneOnOneTemplate = async ({ orgId }) => {
  const res = await getDefaultOneOnOneTemplateApi({ orgId });
  return res.entity;
};

export const putDefaultOneOnOneTemplate = async ({ orgId, templateId }) => {
  const res = await putDefaultOneOnOneTemplateApi({ orgId, templateId });
  return res.entity;
};

export const archiveUnarchiveOneOnOne = async ({ empId, managerId, type }) => {
  if (type === 'ARCHIVE') {
    const res = await archiveOneOnOneApi({ empId, managerId });
    return res.entity;
  }
  const res = await unarchiveOneOnOneApi({ empId, managerId });
  return res.entity;
};

export const getAllEmployeeFeedbacks = async ({ empId, limit, offset }) => {
  const res = await getAllEmployeeFeedbacksApi({ empId, limit, offset });
  return res.entity.map((data) => Post.fromJSON(data.post));
};

export const getAgendaComments = async ({ agendaId, offset, limit }) => {
  const res = await getCommentsApi({
    feedId: agendaId,
    offset,
    limit,
    type: 'AGENDA_ITEM',
  });
  return res.entity.map((commentJSON) => {
    const comment = new PostFeedItemData();
    comment.fromJSON(commentJSON);
    return comment;
  });
};

export const createAgendaComment = async ({
  commentEditorState,
  media,
  visibilityType,
  sentiment,
  isKudos = false,
  agendaId,
}) => {
  const body = getApiBodyFromEditorStateAndMedia(commentEditorState, media, true, sentiment);
  body.visibilityType = visibilityType || POST_VISIBILITY_TYPES.global.name;

  const res = await createCommentApi({
    commentBody: body,
    isKudos,
    feedId: agendaId,
    feedType: 'AGENDA_ITEM',
  });

  const comment = new PostFeedItemData();
  comment.fromJSON(res.entity.comment);
  return { comment };
};

export const updateAgendaComment = async ({
  commentEditorState,
  media,
  visibilityType,
  sentiment,
  isKudos = false,
  commentId,
  agendaId,
}) => {
  const body = getApiBodyFromEditorStateAndMedia(commentEditorState, media, true, sentiment);
  body.visibilityType = visibilityType || POST_VISIBILITY_TYPES.global.name;

  body.uuid = commentId;
  body.type = 'COMMENT';

  const res = await editCommentApi({
    commentId,
    feedId: agendaId,
    commentBody: body,
    feedType: 'AGENDA_ITEM',
    isKudos,
  });

  const comment = new PostFeedItemData();
  comment.fromJSON(res.entity.comment);
  return { comment };
};
